(ns ui.view.counterparty-diligence.responder-form.file-input
  (:require
   [re-frame.core :as rf]
   [common.localization :as loc]
   ["react-dropzone" :refer [useDropzone]]
   [oops.core :as oops]
   [ui.view.counterparty-diligence.responder-form.sub :as sub]
   [ui.util :as uu]
   [reagent.core :as r]
   [clojure.string :as string]
   [ui.components.button.view :as button]
   [common.util :as cu]
   [ui.view.counterparty-diligence.responder-form.validation-error-text :as error-text]))

(defn file-icon-class
  [content-type]
  (let [image? (some-> content-type
                       (string/starts-with? "image"))
        _csv? (= content-type "text/csv")
        pdf? (= content-type "application/pdf")]
    (cond image? "fal fa-image"
          pdf? "fal fa-file-pdf"
          :else "fal fa-file")))

(defn dropzone-inner
  [_]
  (let [file-input-remount-counter* (r/atom 0)]
    (fn [{:keys [add-files multiple?]}]
      (let [hook-result (useDropzone (clj->js {:onDrop add-files
                                               :multiple (boolean multiple?)}))
            root-props (-> hook-result
                           (oops/ocall "getRootProps")
                           (js->clj :keywordize-keys true))
            input-props (-> hook-result
                            (oops/ocall "getInputProps")
                            (js->clj :keywordize-keys true))
            drag-active? (oops/oget hook-result "isDragActive")]
        [:div
         {:class "relative group"}
         [:div
          (merge
           root-props
           {:class (uu/cm "relative rounded flex min-h-24 items-center justify-center border border-dashed py-7 group-hover:bg-complement-light groupöhover:shadow-inner"
                          (when drag-active?
                            "shadow-inner bg-complement-light border-primary-regular"))})
          [:input input-props]]
         [:div
          {:class (uu/cm "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                         (when drag-active?
                           "pointer-events-none"))}
          [:div
           {:class (uu/cm "flex items-center gap-3")}
           [:i
            {:class "fal fa-cloud-upload text-2xl text-base-light-text-paler"}]
           [:span
            [:span
             {:class "text-sm m-0 text-base-light-text-paler"}
             (loc/tr :dropzone-instructions-pre-link nil :en)
             " "
             (loc/tr :or)
             " "]
            [:label
             {:class "text-base-light-text-accent m-0 cursor-pointer text-sm font-medium"}
             (loc/tr :browse-lc)
             ^{:key (str "file-input-v-" @file-input-remount-counter*)}
             [:input
              {:type "file"
               :multiple (boolean multiple?)
               :on-change (fn [e]
                            (let [files (-> e
                                            (oops/oget "target")
                                            (oops/oget "files"))]
                              (add-files files)
                              (swap! file-input-remount-counter* inc)))
               :class "border px-3 py-2 rounded hidden"}]]]]]]))))

(defn dropzone
  [props]
  ;; Make functional so dropzone inner can use the react hook
  [:f> dropzone-inner props])

(defn attachment-item
  [{:keys [attachment open-attachment download-attachment remove-file
           read-only? guest-access-only?]}]
  (let [filename (:attachment/filename attachment)
        temp? (:temp? attachment)]
    [:div
     {:class "flex items-center gap-1 justify-between rounded border px-3 py-2 shadow min-h-11"}
     [:div
      {:class "flex items-center gap-2"}
      [:i {:class "fal fa-file"}]
      [:p filename]]
     (if temp?
       [:div
        {:class "text-sm m-0 text-base-light-text-paler"}
        (loc/tr :uploading.dot.dot)]
       [:div
        {:class "flex items-center gap-1"}
        (when-not guest-access-only?
          [:<>
           [button/square-secondary-hollow
            {:on-click open-attachment
             :tooltip (loc/tr :open-file)}
            [:i {:class "fal fa-external-link"}]]

           [button/square-secondary-hollow
            {:on-click download-attachment
             :tooltip (loc/tr :download-file)}
            [:i {:class "fal fa-cloud-download"}]]])

        (when-not read-only?
          [:div
           [button/square-secondary-hollow
            {:on-click #(remove-file (:db/id attachment))
             :tooltip (loc/tr :remove-file)}
            [:i {:class "fal fa-trash"}]]])])]))

(defn attachment-list
  [{:keys [attachments remove-file download-attachment open-attachment
           read-only? guest-access-only?]}]
  [:div
   {:class "flex flex-col gap-2"}
   (->> attachments
        (map (fn [attachment]
               ^{:key (str (:db/id attachment))}
               [attachment-item
                {:attachment attachment
                 :open-attachment #(open-attachment (:db/id attachment))
                 :download-attachment #(download-attachment (:db/id attachment))
                 :remove-file remove-file
                 :read-only? read-only?
                 :guest-access-only? guest-access-only?}])))])


(defn view
  [{:keys [value question-config add-files read-only? guest-access-only?
           open-attachment download-attachment remove-file state-path
           validation-errors show-validation-errors?]}]
  (let [{:keys [label field multiple? info-text info-text-position]} question-config
        attachments @(rf/subscribe [::sub/field-attachments
                                    {:attachment-ids value
                                     :state-path state-path}])
        uploads @(rf/subscribe [::sub/field-attachment-uploads {:field field
                                                                :state-path state-path}])
        show-dropzone? (and (not read-only?)
                            (or multiple?
                                (and (cu/empty-value? attachments)
                                     (cu/empty-value? uploads))))
        has-info-text? (and (string? info-text)
                            (not (string/blank? info-text)))
        info-text-position (or info-text-position :top)
        info-text-el (when has-info-text?
                       [:p
                        {:class "text-sm m-0 text-base-light-text-paler"}
                        info-text])]
    [:div
     {:class "flex flex-col gap-2"}
     [:label
      {:class "text-[14px] text-input-light-text font-semibold"}
      label]
     (when (= info-text-position :top)
       info-text-el)
     (when (or (seq attachments)
               (seq uploads))
       [:div
        [attachment-list
         {:attachments attachments
          :open-attachment open-attachment
          :download-attachment download-attachment
          :remove-file remove-file
          :read-only? read-only?
          :guest-access-only? guest-access-only?}]
        (when (seq uploads)
          [attachment-list
           {:attachments (->> uploads
                              (mapv (fn [{:keys [temp-id filename]}]
                                      {:db/id temp-id
                                       :attachment/filename filename
                                       :temp? true})))
            :remove-file remove-file
            :read-only? read-only?}])])

     (when show-dropzone?
       [dropzone {:add-files add-files
                  :multiple? multiple?}])

     (when (= info-text-position :bottom)
       info-text-el)

     (when show-validation-errors?
       [error-text/view
        {:validation-errors validation-errors}])]))
